/*
 * The Markdown editor (x-input type="editor"; editorInput in inputs.js starts it): CKEditor 5, fetched when an editor
 * first opens. Its parts, each below: the site's media (Upload Media, External Media), the page's own buttons (Save,
 * Delete, Back to View Mode), photo uploads, alignment kept in Markdown, the toolbar, and how it's set up on the page.
 * Its look: resources/css/vendor/ckeditor (theme.css its colours, content.css the text and toolbar).
 */
import {upload, json as request, UploadError} from './upload.js';
import iconSave from '../../vendor/owenvoke/blade-fontawesome/resources/svg/solid/floppy-disk.svg?raw';
import iconView from '../../vendor/owenvoke/blade-fontawesome/resources/svg/solid/eye.svg?raw';
import iconDelete from '../../vendor/owenvoke/blade-fontawesome/resources/svg/solid/trash-can.svg?raw';

/** The floating toolbar's gap from the top of the screen, as the PDF reader's bar (content.css: .ck-editor__top) */
const TOP = 12;

/** CKEditor's script and stylesheet (copied into the build by vite.config.js), fetched once */
let loading;
const load = () => loading ??= new Promise((resolve, reject) => document.head.append(
    Object.assign(document.createElement('link'), {rel: 'stylesheet', href: '/build/assets/static/ckeditor5/ckeditor5.css'}),
    Object.assign(document.createElement('script'), {src: '/build/assets/static/ckeditor5/ckeditor5.umd.js', onload: resolve, onerror: reject}),
));

/** Opens one of the site's dialogs (x-dialog) after the click that asked, which it would otherwise take as one outside it and close */
export const openDialog = (id) => setTimeout(() => window.dispatchEvent(new CustomEvent(id, {detail: 'open'})));

/*
 * Upload Media and External Media (owner, 2026-10-20: two buttons, one from the computer, one by address). An upload
 * of a photo becomes a Markdown image (with its description); any other upload (PDF, Word file, video, audio, a photo
 * browsers can't show) or a YouTube, Vimeo, video, audio, picture, PDF or Word link becomes a block that the site shows
 * with its own media component (App\Classes\Markdown).
 * In the Markdown it's <div data-media="{token}" data-kind="…" data-label="…"></div>: only the token
 * matters to the site, the kind and label are for the preview here. Pasting a link on its own line
 * does the same as External Media. Files go to media.attach while a placeholder shows the progress.
 * options (x-input.editor): attach (its URL), class and id (whose media), dialog (External Media's id), accept.
 */

/** Photos the editor uploads as Markdown images (the image upload's own types); other files become media blocks */
const PHOTO = /^image\/(jpeg|png|gif|bmp|webp|tiff)$/;
/** A link pasted on its own that becomes media: YouTube, Vimeo, or a video, audio, PDF or Word file */
const MEDIA_LINK = /(youtube\.com|youtu\.be|vimeo\.com)\/|\.(mp4|m4v|webm|mov|ogv|mp3|m4a|aac|wav|ogg|oga|opus|flac|weba|pdf|docx?|odt|rtf)(\?|#|$)/i;
/** What a media block's preview calls it */
const KINDS = {image: 'Photo', video: 'Video', audio: 'Audio', youtube: 'YouTube', vimeo: 'Vimeo', pdf: 'PDF', office: 'Document', file: 'File'};

const mediaPlugin = (options) => class TcrMedia extends CKEDITOR.Plugin {
    static get requires() {
        return [CKEDITOR.Widget];
    }

    init() {
        const editor = this.editor;

        editor.model.schema.register('tcrMedia', {
            inheritAllFrom: '$blockObject',
            allowAttributes: ['token', 'kind', 'label'],
        });

        editor.conversion.for('upcast').elementToElement({
            view: {name: 'div', attributes: {'data-media': true}},
            model: (view, {writer}) => writer.createElement('tcrMedia', {
                token: view.getAttribute('data-media'),
                kind: view.getAttribute('data-kind') || 'file',
                label: view.getAttribute('data-label') || '',
            }),
            converterPriority: 'high',
        });

        editor.conversion.for('dataDowncast').elementToElement({
            model: 'tcrMedia',
            // Empty, so no filler space is saved inside it
            view: (model, {writer}) => writer.createEmptyElement('div', {
                'data-media': model.getAttribute('token') || '',
                'data-kind': model.getAttribute('kind') || 'file',
                'data-label': model.getAttribute('label') || '',
            }),
        });

        // The preview: the media's thumbnail when it has one, and what it is
        editor.conversion.for('editingDowncast').elementToElement({
            model: {name: 'tcrMedia', attributes: ['token', 'kind', 'label']},
            view: (model, {writer}) => {
                const box = writer.createContainerElement('div', {class: 'tcr-media'});
                const preview = writer.createRawElement('div', {class: 'tcr-media__preview'}, (dom) => {
                    const token = model.getAttribute('token');
                    if (token) {
                        const image = Object.assign(document.createElement('img'), {src: `/media/${encodeURIComponent(token)}/file/webp`, alt: ''});
                        image.onerror = () => image.remove();
                        dom.append(image);
                    }
                    dom.append(Object.assign(document.createElement('span'), {
                        textContent: `${KINDS[model.getAttribute('kind')] || 'File'} · ${model.getAttribute('label') || ''}`,
                    }));
                });
                writer.insert(writer.createPositionAt(box, 0), preview);

                return CKEDITOR.toWidget(box, writer, {label: 'Media'});
            },
        });

        editor.ui.componentFactory.add('uploadMedia', (locale) => {
            const view = new CKEDITOR.FileDialogButtonView(locale);
            view.set({acceptedType: options.accept, allowMultipleFiles: true, label: 'Upload Media', icon: CKEDITOR.IconFileUpload, tooltip: true});
            view.on('done', (event, files) => {
                const [photos, others] = Array.from(files).reduce((split, file) => (split[PHOTO.test(file.type) ? 0 : 1].push(file), split), [[], []]);
                photos.length && editor.execute('uploadImage', {file: photos});
                others.forEach((file) => this.send({upload: file}, file.name));
            });

            return view;
        });

        editor.ui.componentFactory.add('externalMedia', (locale) => {
            const button = new CKEDITOR.ButtonView(locale);
            button.set({label: 'External Media', icon: CKEDITOR.IconMedia, tooltip: true});
            button.on('execute', () => openDialog(options.dialog));

            return button;
        });

        // Inserting while a photo or media block is selected adds after it instead of replacing it
        const keepSelected = (event, args) => {
            const selected = editor.model.document.selection.getSelectedElement();
            if (!args[1] && selected && editor.model.schema.isObject(selected)) {
                editor.model.change((writer) => writer.setSelection(selected, 'after'));
            }
        };
        editor.model.on('insertContent', keepSelected, {priority: 'high'});
        editor.model.on('insertObject', keepSelected, {priority: 'high'});

        // The site's External Media dialog hands back the address
        this.onLink = (event) => this.send({url: event.detail}, event.detail);
        window.addEventListener(`${options.dialog}-done`, this.onLink);

        // A link pasted on its own (YouTube, a video, audio, PDF or Word file) becomes media too; pictures stay pictures
        editor.plugins.get('ClipboardPipeline').on('inputTransformation', (event, data) => {
            const uri = (data.dataTransfer.getData('text/uri-list') || '').split(/\r?\n/).find((line) => line && !line.startsWith('#'));
            const text = (uri || data.dataTransfer.getData('text/plain') || '').trim();
            if (/^https?:\/\/\S+$/.test(text) && MEDIA_LINK.test(text)) {
                event.stop();
                this.send({url: text}, text);
            }
        }, {priority: 'high'});
    }

    destroy() {
        window.removeEventListener(`${options.dialog}-done`, this.onLink);
        super.destroy();
    }

    /** Adds a placeholder, sends the file or link, then fills in what the site made of it. */
    send(fields, label) {
        const editor = this.editor;
        let element;
        editor.model.change((writer) => {
            element = writer.createElement('tcrMedia', {kind: fields.upload ? 'file' : 'video', label: `Adding ${label}…`});
            editor.model.insertObject(element, null, null, {setSelection: 'after'});
        });
        const update = (attributes) => editor.model.enqueueChange({isUndoable: false}, (writer) => {
            if (element.root.rootName !== '$graveyard') {
                writer.setAttributes(attributes, element);
            }
        });

        const fail = (message) => {
            editor.model.enqueueChange({isUndoable: false}, (writer) => element.root.rootName !== '$graveyard' && writer.remove(element));
            window.Toaster?.error(message);
        };

        // A file goes in pieces (upload.js), then is added by its signed name; a link is added as it is
        (async () => {
            try {
                const added = fields.upload
                    ? {path: await upload(fields.upload, {progress: (percent) => update({label: `Uploading ${label}… ${percent}%`})})}
                    : {url: fields.url};
                const media = await request(options.attach, {method: 'POST', body: JSON.stringify({...added, class: options.class, id: options.id})});
                update({token: media.token, kind: media.kind, label: media.label});
            } catch (error) {
                fail(error instanceof UploadError ? error.message : 'The upload failed. Check your connection and try again.');
            }
        })();
    }
};

/**
 * The page's own buttons, first in the toolbar in this order (owner, 2026-10-29), each an icon filled in its colour and
 * named in its tooltip: Save (and Ctrl+S / ⌘S; the editor stays open), Delete (the page's confirmation), Back to View
 * Mode (closes the editor). Each optional, a function from editorInput; one that returns a promise greys its button
 * (and its keystroke waits) until it's done. Another is a line here and its function there.
 */
const ACTIONS = {
    save: {label: 'Save', icon: iconSave, keystroke: 'Ctrl+S', class: 'ck-button-action'},
    delete: {label: 'Delete', icon: iconDelete, class: 'tcr-button-danger'},
    close: {label: 'Back to View Mode', icon: iconView, class: 'tcr-button-neutral'},
};

const actionsPlugin = (actions) => class TcrActions extends CKEDITOR.Plugin {
    init() {
        const editor = this.editor;

        Object.entries(actions).forEach(([name, run]) => {
            const {label, icon, keystroke, class: className} = ACTIONS[name];
            const buttons = [];
            let busy = false;
            const press = async () => {
                if (busy) return;
                busy = true;
                buttons.forEach((button) => button.isEnabled = false);
                try {
                    await run();
                } finally {
                    busy = false;
                    buttons.forEach((button) => button.isEnabled = true);
                }
            };

            editor.ui.componentFactory.add(name, (locale) => {
                const button = new CKEDITOR.ButtonView(locale);
                button.set({label, icon, tooltip: true, keystroke, class: className});
                button.on('execute', press);
                buttons.push(button);

                return button;
            });

            keystroke && editor.keystrokes.set(keystroke, (data, cancel) => {
                cancel();
                press();
            });
        });
    }
};

/** Photos chosen, pasted or dropped in the editor go up in pieces too (upload.js); media.upload then makes them a picture */
const uploadAdapter = (url) => function UploadAdapter(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) => ({
        async upload() {
            const path = await upload(await loader.file, {progress: (percent) => Object.assign(loader, {uploadTotal: 100, uploaded: percent})});
            const result = await request(url, {method: 'POST', body: JSON.stringify({path})});
            if (!result.uploaded) throw result.error?.message ?? 'That photo could not be added.';
            return result.urls ?? {default: result.url};
        },
        abort() {},
    });
};

/*
 * Alignment (owner, 2026-10-29): Markdown has none, so an aligned paragraph or heading is saved wrapped in
 * <div data-align="center">, with Markdown inside, which the site shows aligned (.article [data-align]). In the editor
 * it's the block's class (align-center…). Only blocks at the top level: an aligned line inside a list or quote can't be
 * wrapped, so it's saved plain.
 */
const ALIGNMENTS = ['left', 'center', 'right', 'justify'];

/** The editor's HTML on its way to Markdown: each aligned block wrapped in its data-align div, code blocks unnamed */
const toMarkdownHtml = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html;
    template.content.querySelectorAll(ALIGNMENTS.map((name) => `.align-${name}`).join(',')).forEach((block) => {
        const name = ALIGNMENTS.find((name) => block.classList.contains(`align-${name}`));
        block.classList.remove(`align-${name}`);
        block.classList.length || block.removeAttribute('class');
        if (block.parentNode === template.content) {
            const wrapper = document.createElement('div');
            wrapper.dataset.align = name;
            block.replaceWith(wrapper);
            wrapper.append(block);
        }
    });
    // A code block has one kind (config codeBlock): a plain ``` fence, no language named
    template.content.querySelectorAll('code.language-plaintext').forEach((code) => code.removeAttribute('class'));

    return template.innerHTML;
};

/** Markdown's HTML on its way into the editor: each data-align div's blocks take its class, the div goes */
const fromMarkdownHtml = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html;
    template.content.querySelectorAll('div[data-align]').forEach((wrapper) => {
        const name = wrapper.dataset.align;
        ALIGNMENTS.includes(name) && [...wrapper.children].forEach((block) => block.classList.add(`align-${name}`));
        wrapper.replaceWith(...wrapper.childNodes);
    });

    return template.innerHTML;
};

/*
 * Everything written in the editor (pages, blog posts, newsletters, event information, breed
 * standards) is Markdown: the editor reads and saves Markdown, so it offers what Markdown can hold (GitHub's:
 * headings, bold, italic, strikethrough, code and code blocks, quotes, lists and to-do lists nested by indenting,
 * links, pictures, tables, lines) and alignment (above); no colours, fonts or image sizes: the site styles those.
 */
const config = ({uploadUrl, media, actions}) => ({
    licenseKey: 'GPL',

    ui: {
        viewportOffset: {top: TOP, right: TOP, bottom: TOP, left: TOP},
    },

    plugins: [
        CKEDITOR.Essentials,
        CKEDITOR.Markdown,
        CKEDITOR.PasteFromMarkdownExperimental,
        CKEDITOR.PasteFromOffice,
        CKEDITOR.TextTransformation,
        CKEDITOR.Autoformat,
        CKEDITOR.AutoLink,
        mediaPlugin(media),
        actionsPlugin(actions),

        CKEDITOR.Paragraph,
        CKEDITOR.Heading,
        CKEDITOR.Alignment,
        CKEDITOR.Bold,
        CKEDITOR.Italic,
        CKEDITOR.Strikethrough,
        CKEDITOR.Code,
        CKEDITOR.CodeBlock,
        CKEDITOR.BlockQuote,
        CKEDITOR.List,
        CKEDITOR.TodoList,
        CKEDITOR.Indent,
        CKEDITOR.Link,

        CKEDITOR.Image,
        CKEDITOR.AutoImage,
        CKEDITOR.ImageUpload,
        CKEDITOR.ImageToolbar,
        CKEDITOR.ImageTextAlternative,
        uploadAdapter(uploadUrl),

        CKEDITOR.Table,
        CKEDITOR.TableToolbar,
        CKEDITOR.HorizontalLine,
        CKEDITOR.GeneralHtmlSupport,
        CKEDITOR.FindAndReplace,
        CKEDITOR.RemoveFormat,
    ],

    toolbar: {
        // The page's buttons, then the most used first, the rarely used last (owner, 2026-10-29)
        items: [
            ...Object.keys(ACTIONS).filter((name) => actions[name]), ...(Object.keys(actions).length ? ['|'] : []),
            'undo', 'redo',
            '|',
            'heading',
            '|',
            'bold', 'italic', 'link',
            '|',
            'bulletedList', 'numberedList',
            '|',
            'uploadMedia', 'externalMedia',
            '|',
            'alignment', 'blockQuote', 'insertTable', 'horizontalLine',
            '|',
            'strikethrough', 'todoList', 'outdent', 'indent', 'removeFormat',
            // Code and find and replace in a ⋮ menu at the end (owner, 2026-10-29; menuSide below)
            {label: 'Code, Find and Replace', icon: 'threeVerticalDots', items: ['code', 'codeBlock', 'findAndReplace']},
        ],

        // Every button on the bar, no ⋮ menu (owner, 2026-10-29): what doesn't fit goes onto another line
        shouldNotGroupWhenFull: true,
    },

    // Markdown's six levels: the page's title is the <h1>, so a written heading starts at <h2> (##)
    heading: {
        options: [
            {model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph'},
            ...[2, 3, 4, 5, 6].map((level) => ({model: `heading${level - 1}`, view: `h${level}`, title: `Heading ${level - 1}`, class: `ck-heading_heading${level - 1}`})),
        ],
    },

    alignment: {
        options: ALIGNMENTS.map((name) => ({name, className: `align-${name}`})),
    },

    // Code without a language (Markdown's ``` fence)
    codeBlock: {
        languages: [{language: 'plaintext', label: 'Code'}],
    },

    image: {
        toolbar: ['imageTextAlternative'],
    },

    table: {
        contentToolbar: ['tableColumn', 'tableRow'],
    },

    // An embedded video (YouTube) is kept as HTML
    htmlSupport: {
        allow: [{name: 'iframe', attributes: true, classes: true, styles: true}],
    },
});

/*
 * Its set-up on the page, each step after CKEditor has made it
 */

/** Markdown in and out: embedded videos (iframe) and the site's blocks (div: media, alignment) kept as HTML, and
    alignment wrapped and unwrapped (above) around the Markdown plugin's own converters */
const markdown = (editor) => {
    const processor = editor.data.processor;
    processor.keepHtml('iframe');
    processor.keepHtml('div');
    const toMarkdown = processor._html2markdown.parse.bind(processor._html2markdown);
    processor._html2markdown.parse = (html) => toMarkdown(toMarkdownHtml(html));
    const fromMarkdown = processor._markdown2html.parse.bind(processor._markdown2html);
    processor._markdown2html.parse = (markdown) => fromMarkdownHtml(fromMarkdown(markdown));
};

/** The toolbar floats as the PDF reader's bar does (owner, 2026-10-29): sticky in the page (content.css), so nothing is
    left behind (CKEditor's own lifts it out and leaves a gap); .floating, rounded with a shadow, once it's floating */
const floatingToolbar = (editor) => {
    const panel = editor.ui.view.stickyPanel;
    panel.unbind('isActive');
    panel.isActive = false;
    const root = editor.ui.view.element;
    const top = root.querySelector('.ck-editor__top');
    const float = () => top.classList.toggle('floating', root.getBoundingClientRect().top < TOP);
    window.addEventListener('scroll', float, {passive: true, capture: true});
    editor.on('destroy', () => window.removeEventListener('scroll', float, {capture: true}));
};

/** The ⋮ menu, last in the toolbar, opens towards the side with room (CKEditor's own choice ran off a phone's screen) */
const menuSide = (editor) => {
    const menu = editor.ui.view.toolbar.items.last;
    menu.class = 'tcr-more';
    menu.on('change:isOpen', () => {
        menu.panelPosition = menu.element.getBoundingClientRect().left > window.innerWidth / 2 ? 'sw' : 'se';
    }, {priority: 'high'});
};

/** Written as it will show (owner, 2026-10-29): the text area takes the page's classes (Livewire\Article, Text,
    newsletters), and with reading the page's reading column (.tcr-reading, content.css) */
const asShown = (editor, reading) => editor.editing.view.change((writer) => {
    const root = editor.editing.view.document.getRoot();
    ['article', 'prose', 'prose-invert', 'max-w-none', ...(reading ? ['tcr-reading'] : [])].forEach((name) => writer.addClass(name, root));
});

/**
 * Starts the editor on element (x-input.editor via editorInput): model.get() is the Markdown to start from, model.set()
 * receives every change. uploadUrl: where photos go (media.upload); media: the media options (above); actions: the
 * page's buttons (above); reading: the text in the page's reading column. Unsaved changes ask before the page is left
 * (allowPageRefresh() on save clears it).
 */
export const initEditor = async (element, model, {uploadUrl = null, media = {}, actions = {}, reading = false} = {}) => {
    await load();
    const editor = await CKEDITOR.ClassicEditor.create(element, config({uploadUrl, media, actions}));

    markdown(editor);
    floatingToolbar(editor);
    menuSide(editor);
    asShown(editor, reading);

    editor.setData(model.get() ?? '');
    editor.model.document.on('change:data', () => {
        model.set(editor.getData());
        window.onbeforeunload = () => 'You have unsaved changes.';
    });

    return editor;
};
